import { TrendingUp, Users } from "lucide-react";

interface CagnotteMoment {
  event: string;
  proches?: number;
  montant?: string;
  futur?: string;
  total?: string;
  source?: string;
}

interface TimelineCardProps {
  ageRange: string;
  title: string;
  image: string;
  milestones: string[];
  features: string[];
  testimonial: {
    text: string;
    author: string;
    role: string;
  };
  badge: string;
  color: string;
  index: number;
  cagnotteMoments?: CagnotteMoment[];
  totalInvesti?: string;
  totalFutur?: string;
  croissance?: string;
}

const TimelineCard = ({
  ageRange,
  title,
  image,
  milestones,
  features,
  testimonial,
  badge,
  color,
  index,
  cagnotteMoments,
  totalInvesti,
  totalFutur,
  croissance,
}: TimelineCardProps) => {
  return (
    <div
      className="w-[340px] md:w-[400px] bg-white rounded-3xl shadow-xl overflow-hidden transform hover:scale-105 transition-all duration-300"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Age Badge */}
      <div className={`bg-${color} text-white text-center py-3 font-bold text-lg`}>
        {ageRange}
      </div>

      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={image}
          alt={title}
          width="400"
          height="192"
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <h3 className="absolute bottom-4 left-4 text-white text-2xl font-bold">
          {title}
        </h3>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Milestones */}
        <div>
          <p className="text-sm font-semibold text-muted-foreground mb-2">Étapes clés :</p>
          <div className="flex flex-wrap gap-2">
            {milestones.map((milestone, idx) => (
              <span
                key={idx}
                className="text-xs bg-muted px-3 py-1 rounded-full"
              >
                {milestone}
              </span>
            ))}
          </div>
        </div>

        {/* Cagnottes-Moments Section */}
        {cagnotteMoments && cagnotteMoments.length > 0 && (
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-primary mb-3">💝 Cagnottes-moments créées :</p>
            <div className="space-y-2">
              {cagnotteMoments.map((moment, idx) => (
                <div key={idx} className="bg-sunrise-light/30 p-3 rounded-lg flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{moment.event}</p>
                    {moment.proches && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Users className="h-3 w-3" />
                        <span>{moment.proches} proches</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    {moment.montant && (
                      <p className="text-sm font-bold text-primary">{moment.montant}</p>
                    )}
                    {moment.total && (
                      <p className="text-sm font-bold text-primary">{moment.total}</p>
                    )}
                    {moment.futur && (
                      <p className="text-xs text-growth flex items-center gap-1 justify-end">
                        <TrendingUp className="h-3 w-3" />
                        {moment.futur}
                      </p>
                    )}
                    {moment.source && (
                      <p className="text-xs text-muted-foreground italic">{moment.source}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Total Summary */}
            {totalInvesti && totalFutur && (
              <div className="mt-3 bg-legatia-green/10 p-4 rounded-xl">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold">Total investi :</span>
                  <span className="text-lg font-bold text-primary">{totalInvesti}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold">Valeur à 18 ans :</span>
                  <span className="text-lg font-bold text-growth">{totalFutur}</span>
                </div>
                {croissance && (
                  <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t border-white/50">
                    <TrendingUp className="h-4 w-4 text-growth" />
                    <span className="text-sm font-bold text-growth">{croissance} de croissance</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Features */}
        <div>
          <p className="text-sm font-semibold text-muted-foreground mb-2">Avec Legatia :</p>
          <div className="space-y-1">
            {features.map((feature, idx) => (
              <p key={idx} className="text-sm">
                {feature}
              </p>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <div className="bg-muted/50 p-4 rounded-xl">
          <p className="text-sm italic mb-2">"{testimonial.text}"</p>
          <p className="text-xs font-semibold">
            — {testimonial.author}
            {testimonial.role && `, ${testimonial.role}`}
          </p>
        </div>

        {/* Badge */}
        <div className={`bg-${color} text-white text-center py-2 px-4 rounded-full font-bold`}>
          {badge}
        </div>
      </div>
    </div>
  );
};

export default TimelineCard;